import { graphql } from 'react-relay';
import type { ComponentType } from 'react';
import React, { useCallback, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import Button from '@atlaskit/button/custom-theme-button';
import { default as NewButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useFlags } from '@atlaskit/flag';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { token } from '@atlaskit/tokens';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';

import type {
	RecommendationTileFeedFollowRecommendationMutation as RecommendationTileFeedFollowRecommendationMutation$data,
	RecommendationTileFeedFollowRecommendationMutationVariables as RecommendationTileFeedFollowRecommendationMutation$variables,
} from './__types__/RecommendationTileFeedFollowRecommendationMutation.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useFeedFollowFeedFollowQuery } from '@confluence/profile/entry-points/useFeedFollowFeedFollowQuery.ts';

const RecommendationTileFeedFollowRecommendationMutation = graphql`
	mutation RecommendationTileFeedFollowRecommendationMutation(
		$followUser: [ID]
		$followSpace: [Long]
		$unfollowUser: [ID]
		$unfollowSpace: [Long]
	) {
		setFeedUserConfig(
			input: {
				followUsers: $followUser
				followSpaces: $followSpace
				unfollowUsers: $unfollowUser
				unfollowSpaces: $unfollowSpace
			}
		) {
			success
		}
	}
`;
const i18n = defineMessages({
	follow: {
		id: 'feed.recommendation.tile.follow',
		defaultMessage: 'Follow',
		description: 'Button text to feed follow a recommended entity',
	},
	followLabel: {
		id: 'feed.recommendation.tile.follow.label',
		defaultMessage: 'Follow {name}',
		description: 'Aria label for Button text to feed follow a recommended entity',
	},
	space: {
		id: 'feed.recommendation.tile.space',
		defaultMessage: 'Space',
		description: 'Label text for the space recommendation tile',
	},
	dismissLabel: {
		id: 'feed.recommendation.tile.dismiss.label',
		defaultMessage: 'Dismiss {name} recommendation',
		description: 'Label text for the button to dismiss the recommendation',
	},
	flagFollowTitle: {
		id: 'feed.recommendation.flag.follow.success',
		defaultMessage: 'Followed {name}',
		description: 'Text to display for the title of the follow flag',
	},
	flagFollowAction: {
		id: 'feed.recommendation.flag.follow.action',
		defaultMessage: 'Dismiss',
		description: 'Text to display for the action of the follow flag to dismiss the flag',
	},
	flagErrorTitle: {
		id: 'feed.recommendation.flag.follow.error',
		defaultMessage: 'Could not follow {name}',
		description: 'Text to display for the title of the follow error flag',
	},
	flagErrorDescription: {
		id: 'feed.recommendation.flag.follow.error.description',
		defaultMessage: 'Please try again later',
		description: 'Text for the follow error flag description',
	},
	profileTile: {
		id: 'feed.recommendation.tile.profile',
		defaultMessage: '{name} profile',
		description: 'Aria label for the profile tile',
	},
});

export const TILE_MARGIN = 16;
export const PAGE_SIZE = 4;
const TILE_WIDTH_PERCENT = 100 / PAGE_SIZE;
const TILE_MARGIN_WIDTH = TILE_MARGIN * ((PAGE_SIZE - 1) / PAGE_SIZE);

const fullSizeButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			position: 'absolute',
			width: '100%',
			height: '100%',
		},
		spinnerStyles,
	};
};

const dismissTileButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			position: 'absolute',
			top: token('space.050'),
			right: token('space.050'),
			width: '28px',
			height: '28px',
		},
		spinnerStyles,
	};
};

const tileWrapperStyle = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'space-evenly',
	height: '154px',
	flexShrink: '0',
	minWidth: '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	width: `calc(${TILE_WIDTH_PERCENT}% - ${TILE_MARGIN_WIDTH}px)`,
	scrollSnapAlign: 'start',
	background: token('color.background.input'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	padding: token('space.100'),
	textAlign: 'center',
	pointerEvents: 'auto',
	marginRight: `${TILE_MARGIN}px`,
	opacity: '1',
});

const titleHover = css({
	'&:hover': {
		background: token('color.background.input.hovered'),
	},
});

const loadingStyle = css({
	opacity: 0.5,
});

const userIconStyle = css({
	width: '32px',
	height: '32px',
	borderRadius: token('radius.full'),
});

const nameAndTypeWrapperStyle = css({
	width: '100%',
});

const nameWrapperStyle = css({
	font: token('font.body'),
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	position: 'relative',
});

const titlePlaceholderStyle = css({
	height: '14px',
});

const titleOrTypeWrapperStyle = css({
	color: token('color.text.subtle'),
	font: token('font.body.small'),
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
	position: 'relative',
});

type RecommendationTileProps = {
	name: string;
	id: string;
	url: string;
	icon: string;
	type: 'user' | 'space';
	title?: string;
	dismissTile: (id: string) => void;
	cardIndex: number;
	position: number;
};

export const RecommendationTile: ComponentType<RecommendationTileProps> = ({
	name,
	id,
	url,
	icon,
	type,
	title,
	dismissTile,
	cardIndex,
	position,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showFlag } = useFlags();
	const [tileHover, setTileHover] = useState(true);

	const enableTileHover = useCallback(() => {
		setTileHover(true);
	}, [setTileHover]);

	const disableTileHover = useCallback(() => {
		setTileHover(false);
	}, [setTileHover]);

	const onFollowSuccess = useCallback(() => {
		dismissTile(id);
		showFlag({
			title: <FormattedMessage {...i18n.flagFollowTitle} values={{ name }} />,
			icon: (
				<StatusSuccessIcon color={token('color.icon.success')} label="Success" spacing="spacious" />
			),
			isAutoDismiss: true,
		});
	}, [dismissTile, id, name, showFlag]);

	const onFollowError = useCallback(() => {
		showFlag({
			title: <FormattedMessage {...i18n.flagErrorTitle} values={{ name }} />,
			description: <FormattedMessage {...i18n.flagErrorDescription} />,
			icon: <StatusErrorIcon label="error" color={token('color.icon.danger')} spacing="spacious" />,
			isAutoDismiss: true,
		});
	}, [name, showFlag]);
	const [changeFeedFollows, { loading: followLoading, error: followError }] = useMutationInterop<
		RecommendationTileFeedFollowRecommendationMutation$data,
		RecommendationTileFeedFollowRecommendationMutation$variables
	>(RecommendationTileFeedFollowRecommendationMutation, {
		onCompleted: onFollowSuccess,
		onError: onFollowError,
		refetchQueries: [
			{
				query: useFeedFollowFeedFollowQuery,
			},
		],
	});
	const [dismissRecTile, { loading: unfollowLoading, error: unfollowError }] = useMutationInterop<
		RecommendationTileFeedFollowRecommendationMutation$data,
		RecommendationTileFeedFollowRecommendationMutation$variables
	>(RecommendationTileFeedFollowRecommendationMutation);
	const handleTileLinkButton = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'item',
				actionSubjectId: 'tile',
				source: 'suggestionsCard',
				attributes: {
					settingId: id,
					settingType: type,
					recommendationRank: cardIndex,
					pos: position,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, id, type, cardIndex, position]);

	const handleTileDismissButton = useCallback(async () => {
		void dismissRecTile({
			variables:
				type === 'user' ? { unfollowUser: [id] } : { unfollowSpace: [id as unknown as number] },
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				source: 'suggestionsCard',
				attributes: {
					settingId: id,
					settingType: type,
					recommendationRank: cardIndex,
					pos: position,
				},
			},
		}).fire();
		dismissTile(id);
	}, [dismissTile, id, dismissRecTile, type, createAnalyticsEvent, cardIndex, position]);

	const handleFollowButton = useCallback(() => {
		void changeFeedFollows({
			variables:
				type === 'user' ? { followUser: [id] } : { followSpace: [id as unknown as number] },
		});
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'follow',
				source: 'suggestionsCard',
				attributes: {
					settingId: id,
					settingType: type,
					recommendationRank: cardIndex,
					pos: position,
				},
			},
		}).fire();
	}, [changeFeedFollows, type, id, createAnalyticsEvent, cardIndex, position]);

	const titleOrType = type === 'user' ? title : intl.formatMessage(i18n.space);

	const renderFollowButton = () => {
		return (
			<NewButton
				onClick={handleFollowButton}
				onMouseEnter={disableTileHover}
				onMouseLeave={enableTileHover}
				appearance="primary"
				isDisabled={followLoading}
				aria-label={intl.formatMessage(i18n.followLabel, { name })}
			>
				<FormattedMessage {...i18n.follow} />
			</NewButton>
		);
	};

	return (
		<div
			css={[
				tileWrapperStyle,
				(unfollowLoading || followLoading) && loadingStyle,
				tileHover && titleHover,
			]}
			data-testid="feed-rec-tile-wrapper"
		>
			{followError && <ErrorDisplay error={followError} />}
			{unfollowError && <ErrorDisplay error={unfollowError} />}
			<Button
				href={url}
				onClick={handleTileLinkButton}
				target="_blank"
				appearance="link"
				theme={fullSizeButtonTheme}
				testId="feed-rec-tile-link"
				aria-label={intl.formatMessage(i18n.profileTile, { name })}
			/>
			<Button
				appearance="subtle"
				theme={dismissTileButtonTheme}
				onClick={handleTileDismissButton}
				onMouseEnter={disableTileHover}
				onMouseLeave={enableTileHover}
				spacing="none"
				iconBefore={<CrossIcon label={intl.formatMessage(i18n.dismissLabel, { name })} />}
				isDisabled={unfollowLoading}
			/>
			{type === 'user' ? (
				<img css={userIconStyle} src={icon} alt="" />
			) : (
				<SpaceIcon src={icon} size="medium" />
			)}
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
			<div css={nameAndTypeWrapperStyle}>
				<Tooltip content={name}>
					<div css={nameWrapperStyle}>{name}</div>
				</Tooltip>
				<div css={titlePlaceholderStyle}>
					<Tooltip content={titleOrType}>
						<div css={titleOrTypeWrapperStyle}>{titleOrType}</div>
					</Tooltip>
				</div>
			</div>
			{renderFollowButton()}
		</div>
	);
};
